import {
  FileUploadCompleteEvent,
  FileUploadProgressEvent,
} from '@ublend-npm/multipart-uploader';
import {
  AssignmentDetails,
  InvalidAssignmentDetails,
  Lti1p3AssignmentDetails,
} from './components/Assignment/assignmentType';
import { Meta } from './lib/fetchMeta';
import { quizType } from './plugins/quiz/quizType';

/**
 * All entities should extend from this type
 *
 * if data is not defined, it means the entity is still being loaded
 */
type Entity<T extends string, D> = {
  type: T;
  data?: D;
  transient?: unknown;
  failed?: boolean;
};

export type MetaEntity = Entity<'meta', Meta> & {
  url: string;
  expanded?: boolean;
};

export interface AssignmentMetaData {
  assignmentId: string;
}

export type AssignmentEntity = Entity<'assignment', AssignmentMetaData> & {
  assignmentId: string;
  savedAssignmentDetails?: {
    assignmentDetails:
      | InvalidAssignmentDetails
      | AssignmentDetails
      | Lti1p3AssignmentDetails
      | null;
    isEducator: boolean;
  };
};

export type FileEntity = Entity<'file', FileUploadCompleteEvent> & {
  file: Pick<File, 'name' | 'type' | 'lastModified' | 'size'> & {
    /**
     * needed to distinguish regular audio files from voice recordings
     */
    isRecording?: boolean;
  };
  transient?: FileUploadProgressEvent;
  expanded?: boolean;
};

export interface LtiMetaBase {
  contentId?: string;
  providerId?: string;
}

/**
 * Add your types as optionals here as you introduce a new LTI integration
 */
export interface LtiParamBase {
  launchType?: LtiLaunchType;
  contentId?: string;
  contentItem?: {
    type: 'ltiResourceLink';
    url: string;
  };
}

export type LtiEntity<
  T extends LtiMetaBase = LtiMetaBase,
  P extends LtiParamBase = LtiParamBase,
> = Entity<'lti', T> & {
  linkId?: string;
  params?: P;
};

export interface ItemSelected {
  reference: string;
}
export type QuizData = { item?: ItemSelected };
export type QuizEntity = Entity<typeof quizType, QuizData> & { data: QuizData };

export type AulaEntity =
  | MetaEntity
  | FileEntity
  | LtiEntity
  | AssignmentEntity
  | QuizEntity;

export type AulaEntities = {
  [key: string]: AulaEntity;
};

export enum LtiLaunchType {
  BASIC = 'basic',
  CONTENT_SELECTION = 'content-selection',
  DEEP_LINKING = 'deep-linking',
}

export enum LtiRequestType {
  RESOURCE_LINK = 'LtiResourceLinkRequest',
  DEEP_LINKING = 'LtiDeepLinkingRequest',
}

export const isAulaEntity = (object: any): object is AulaEntity => object?.type;
